// Models
import {MixedContentItemDTO} from "../../Models/DTOs/CountWrapper";
import ICarouselContentDTO from "../../Models/DTOs/ICarouselContentDTO";
import VideoDTO, {ContinueWatchingItem} from "../../Models/DTOs/VideoDTO";
import {ContentAccessType} from "../../Models/Enums/ContentAccessType";
import {ContentType} from "../../Models/Enums/ContentType";
import SeriesDTO from "../../Models/DTOs/SeriesDTO";

export function transformSeriesToCarouselContent(series: SeriesDTO): ICarouselContentDTO {
    return {
        Id: series.Id,
        Title: series.Title ?? "",
        Text: series.Description ?? "",

        // Series isn't an episode, so default these
        Season: 0,
        Episode: 0,

        // You have both CategoryId and VideoCategoriesId on Series; pick the one your UI expects.
        // Your Video transform uses VideoCategoriesId -> CategoryId, so keep consistent:
        CategoryId: (series as any).VideoCategoriesId ?? series.CategoryId ?? 0,

        Thumbnail: (series as any).HeaderImg ?? "",               // landscape
        PortraitThumbnail: (series as any).SeriesCover ?? "",     // portrait
        BackgroundImage: (series as any).BackgroundImage ?? (series as any).HeaderImg ?? "",

        AccessType: ContentAccessType.Unlimited,
        ContentType: ContentType.Series, // <-- if you have this enum value; otherwise choose what your UI uses for Series

        Created: (series as any).Datetime,
        ReleaseDate: (series as any).Datetime,
        ReleaseDateEarlyAccess: "1900-01-01T00:00:00Z", // or null if your DTO supports null

        IsOriginal: (series as any).IsOriginal ?? false,
        IsFreeView: false,
        IsFreeViewApp: false,

        IsVisible: true,
        IsFavourite: false,

        Rating: (series as any).Rating ?? 0,

        IsBookable: false,
        IsClickeable: true,

        // Series-specific extra (if your ICarouselContentDTO supports it)
        LatestEpisode: undefined,

        VideoLinkHLS: "",
        VideoLinkDash: "",
        TrailerLinkHLS: "",
        TrailerLinkDash: "",

        // For Series, "Link" usually should navigate to series page, not a stream URL.
        // If your SeriesDTO has a Link field, use it; otherwise empty and let the click handler route by Id.
        Link: (series as any).Link ?? "",

        OptionalLabel: "",
    };
}

export function transformVideoToCarouselContent(
    video: VideoDTO
): ICarouselContentDTO {
    return {
        Id: video.Id,
        Title: video.Title,
        Text: video.Description,                                                    // Since ICarouselContentDTO has "Text" instead of "Description"
        Season: video.Season ?? 0,
        Episode: video.Episode,
        CategoryId: video.VideoCategoriesId ?? 0,

        Thumbnail: video.Thumbnail,
        PortraitThumbnail: video.PortraitThumbnail,
        BackgroundImage: video.BackgroundImage,

        // For fields, you might not have a direct mapping for, you can assign defaults or handle them
        AccessType: ContentAccessType.Unlimited,                                    // or from video-single.AccessLevel if you have a mapping
        ContentType: video.SeriesId ? ContentType.Episode : ContentType.Film,       // or from logic that decides if it’s a film / series
        Created: video.Datetime,                                                    // or a new Date().toISOString() if needed
        ReleaseDate: video.Datetime,                                                // pick from Datetime or DatetimeEarlyAccess
        ReleaseDateEarlyAccess: video.DatetimeEarlyAccess,

        IsOriginal: video.IsOriginal,
        IsFreeView: video.IsFreeView,
        IsFreeViewApp: video.IsFreeViewApp,
        // Depending on your logic, might default these to `true` if not available:
        IsVisible: true,
        IsFavourite: false,

        Rating: video.Rating,
        IsBookable: false,
        IsClickeable: true,
        LatestEpisode: undefined,

        // If your API provides separate HLS and Dash links, put them here.
        // Otherwise, set them to empty or derive from something else in video-single.
        VideoLinkHLS: "",
        VideoLinkDash: "",
        TrailerLinkHLS: "",
        TrailerLinkDash: "",

        Link: "", // Full playback URLs are supplied only by playback authorization.
        OptionalLabel: ''
    };
}

export function transformContinueWatchingToCarouselContent(
    cw: ContinueWatchingItem
): ICarouselContentDTO {
    const baseItem = transformVideoToCarouselContent(cw.Video);

    let percentWatched = 0;

    // Use the integer field from the DB instead of the Runtime string
    const durationSeconds = cw.Video.VideoRuntimeLengthSec;

    if (durationSeconds && durationSeconds > 0 && cw.Peak > 0) {
        // Calculate percentage and clamp it between 0 and 100
        percentWatched = Math.floor(Math.min((cw.Peak / durationSeconds) * 100, 100));
    }

    return {
        ...baseItem,
        Peak: cw.Peak,
        // If we don't have a valid duration, we pass undefined so the UI
        // knows not to render a broken progress bar.
        PercentWatched: percentWatched > 0 ? percentWatched : undefined
    };
}

export function transformMixedItemToCarouselContent(
    item: MixedContentItemDTO
): ICarouselContentDTO | null {
    if (item.Type === "video" && item.Video) {
        return transformVideoToCarouselContent(item.Video);
    }

    if (item.Type === "series" && item.Series) {
        return transformSeriesToCarouselContent(item.Series);
    }

    return null;
}
